/**
 * This is mapping for each field that will be sent to Marketo
 * This is also the internal names used by the input fields
 */
export const marketoFields = {
  subject: 'affordabilityCalculatorSubject',
  students: 'affordabilityCalculatorStudsperyear',
  level: 'affordabilityCalculatorLevelofclasses',
  cost: 'affordabilityCalculatorCurrenttextbookcost',
  affordability: 'affordabilityCalculatorImportanceofcost'
};

export const subjects = [
  'Biology and Health Sciences',
  'Business and Management',
  'Chemistry',
  'Communications and Media',
  'Economics',
  'Education',
  'Engineering and Computer Science',
  'English',
  'Geography and Earth Sciences',
  'History',
  'Languages',
  'Law',
  'Mathematics and Statistics',
  'Music and Performing Arts',
  'Philosophy',
  'Physics',
  'Political Sciences',
  'Psychology',
  'Religious Studies',
  'Sociology and Anthropology'
];

export const levels = [
  'Intro', 'Advanced', 'Graduate'
];

export const bookPrices = {
  'Biology and Health Sciences': 24.62,
  'Business and Management': 37.60,
  'Chemistry': 38.33,
  'Communications and Media': 27.73,
  'Economics': 31.00,
  'Education': 0.00,
  'Engineering and Computer Science': 12.50,
  'English': 25.00,
  'Geography and Earth Sciences': 36.78,
  'History': 35.00,
  'Languages': 42.50,
  'Law ': 25.00,
  'Mathematics and Statistics': 11.00,
  'Music and Performing Arts': 41.25,
  'Philosophy': 0.00,
  'Physics': 0.00,
  'Political Sciences': 26.00,
  'Psychology': 11.67,
  'Religious Studies': 40.00,
  'Sociology and Anthropology': 22.50,
};

/**
 * This contains the mappings for the Results page. Each savings range contains a different
 * copy and different image.
 * @type {*[]}
 */
export const resultMappings = [{
  min: -Infinity,
  max: 500,
  savings: '$500',
  subheading: 'You\'re already an affordable classroom!',
  copy: 'But you’re already doing a great job creating an affordable learning environment for your class. ' +
  'It’s not easy to get to a place where your students can both afford and enjoy course materials. ' +
  'Congrats on doing a good job of it!',
  image: 'universeGraphic'
}, {
  min: 501,
  max: 1000,
  savings: '$1000',
  subheading: 'You\'re on the right track, but your students could still be saving more',
  copy: 'Compared to many classrooms, ' +
  'you’re doing great—but there’s still a few ways you can help save them some extra cash. ' +
  'We’ve recommended some titles below that are relevant to the subject you teach.',
  image: 'piggyBankGraphic'
}, {
  min: 1001,
  max: 5000,
  savings: '$5000',
  subheading: 'You have lots of room for savings in your classroom!',
  copy: 'That’s a lot of money, but we know how difficult it can be to find affordable and engaging textbooks. ' +
  'Quality versus cost is a fine balance to strike. ' +
  'But we’ve got some ideas for getting high quality, affordable texts into the hands of your students. ' +
  'Check out a few of our recommendations below!',
  image: 'scaleGraphic'
}, {
  min: 5001,
  max: Infinity,
  savings: '$5000+',
  subheading: 'Whoa. Your students are big spenders!',
  copy: 'For that kind of money they could buy themselves a car! ' +
  'Help put them in the driver’s seat with some high-quality, affordable textbooks. ' +
  'Check out this list of great recommendations—they’ll help get you and your students started down the road to savings.',
  image: 'stickShiftGraphic'
}];

/**
 * This defines which order each screen shows up. Only ProgressPage type pages are customizable
 * with input fields. Splash and EmailSubmission are non-customizable; Results page is dynamic based on the results.
 */

export const storyboard = [
  {
    type: 'Splash',
    props: {
      backgroundImage: 'splashBg',
    }
  },
  {
    type: 'ProgressPage',
    props: {
      header: "What subject area do you teach in?",
      backgroundImage: 'frame1Bg',
      didYouKnow: "The average annual cost of books for full-time students is $1,298—that's almost one month room and board",
      input: {
        component: 'select',
        name: marketoFields.subject,
        options: [{
          label: 'I teach in...',
          value: ''
        }, ...subjects.map((s) => ({
          label: s,
          value: s
        }))]
      }
    }
  },
  {
    type: 'ProgressPage',
    props: {
      header: "How many students do you typically teach per year?",
      backgroundImage: 'frame2Bg',
      didYouKnow: "29.7% of students use financial aid to purchase textbooks",
      input: {
        component: 'input',
        type: 'number',
        name: marketoFields.students,
        placeholder: 'ex. 2500',
        min: 0,
        max: 1000000000,
        pattern: "[0-9]*"
      },
      addon: 'students',
      addonType: 'post'
    }
  },
  {
    type: 'ProgressPage',
    props: {
      header: "What level of classes do you typically teach?",
      backgroundImage: 'frame3Bg',
      didYouKnow: "If every full-time student in the U.S. could save just $100 per semester on textbooks it would represent a total savings of $1 billion",
      input: {
        component: 'select',
        name: marketoFields.level,
        options: [{
          label: 'I typically teach...',
          value: ''
        }, ...levels.map((l) => ({
          label: l,
          value: l
        }))]
      }
    }
  },
  {
    type: 'ProgressPage',
    props: {
      header: "How much does your current textbook cost at your bookstore?",
      backgroundImage: 'frame4Bg',
      didYouKnow: "Of the 65% of students who have skipped buying a textbook because it was too expensive, 94% were concerned that doing so would hurt their grades -- but they did it anyway",
      input: {
        component: 'input',
        type: 'number',
        name: marketoFields.cost,
        placeholder: 'ex. 536',
        min: 0,
        max: 1000000000,
        pattern: "[0-9]*"
      },
      addon: '$',
      addonType: 'pre'
    }
  },
  {
    type: 'ProgressPage',
    props: {
      header: "How important is affordability for students in your textbook?",
      backgroundImage: 'frame5Bg',
      didYouKnow: "87% of students feel they'd get better grades with interactive textbooks",
      input: {
        component: 'input',
        type: 'radio',
        name: marketoFields.affordability,
        options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((v) => ({ label: v, value: v }))
      }
    }
  },
  {
    type: 'EmailSubmission',
    props: {
      backgroundImage: '' // blank background
    }
  },
  {
    type: 'Results',
    props: {
      backgroundImage: 'resultsBg',
    }
  }
];


export const validate = values => {
  const errors = {};
  if (!values[marketoFields.subject] || values[marketoFields.subject] === null) {
    errors[marketoFields.subject] = true
  }
  if (!values[marketoFields.students]) {
    errors[marketoFields.students] = true
  }
  if (!values[marketoFields.level] || values[marketoFields.level] === '') {
    errors[marketoFields.level] = true
  }
  if (!values[marketoFields.cost]) {
    errors[marketoFields.cost] = true
  }
  if (!values[marketoFields.affordability] || values[marketoFields.affordability] === '') {
    errors[marketoFields.affordability] = true
  }
  if (!values.email) {
    errors.email = 'required'
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = 'invalid-email'
  }
  return errors
};


// WEBPACK FOOTER //
// ./src/data/index.js